import { CreateDigest, UpdateDigest } from '@front-app/api-generated/digests-api';
import { Group } from '@front-app/services/GroupsApi/GroupModel';
import { chunk } from '@front-app/util/array';
import cloneDeep from 'clone-deep';
import addYears from 'date-fns/addYears/index';
import format from 'date-fns/format/index';
import isFuture from 'date-fns/isFuture/index';
import parseDate from 'date-fns/parse/index';
import { DigestFormOutput } from '../components/digestForm';
import {
	ApiDigestResponse,
	DigestMonthlyTiming,
	DigestTimingDay,
	FullDigest,
	MonthlyTimingKey,
} from '../model';
import { allMonthlyTimingKeys, createBlankMonthlyTiming } from './scheduling';

export function toFullDigest(input: ApiDigestResponse): FullDigest {
	return {
		...cloneDeep(input),
		startDate: digestDateToIso(input.startDate),
		endDate: digestDateToIso(input.endDate),
		nextEmailDate: input.nextEmailDate ? digestDateToIso(input.nextEmailDate) : input.nextEmailDate,
		timingMonthly: toDigestMonthlyTiming(input.timingMonthly),
	};
}

const DIGEST_API_DATE_FORMAT = 'yyyy-MM-dd';

export function digestDateToIso(input: string): string {
	try {
		// The year is needed for some reason, or the hours/minutes/seconds will be off
		return parseDate(input, DIGEST_API_DATE_FORMAT, new Date(2020, 0, 0, 0, 0, 0)).toISOString();
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(`Error parsing digest date response for "${input}"`, e);
		throw e;
	}
}

export function isoDateToDigestDate(input: string): string {
	return format(new Date(input), DIGEST_API_DATE_FORMAT);
}

const DAY_OF_WEEK_DIGIT_TO_NAME: Record<string, DigestTimingDay> = {
	0: DigestTimingDay.SUNDAY,
	1: DigestTimingDay.MONDAY,
	2: DigestTimingDay.TUESDAY,
	3: DigestTimingDay.WEDNESDAY,
	4: DigestTimingDay.THURSDAY,
	5: DigestTimingDay.FRIDAY,
	6: DigestTimingDay.SATURDAY,
};

const WEEK_NUMBER_TO_NAME: Record<string, keyof DigestMonthlyTiming> = {
	1: 'firstWeek',
	2: 'secondWeek',
	3: 'thirdWeek',
	4: 'fourthWeek',
	5: 'lastWeek',
};

const NAME_TO_DAY_OF_WEEK_DIGIT: Record<DigestTimingDay, number> = {
	[DigestTimingDay.SUNDAY]: 0,
	[DigestTimingDay.MONDAY]: 1,
	[DigestTimingDay.TUESDAY]: 2,
	[DigestTimingDay.WEDNESDAY]: 3,
	[DigestTimingDay.THURSDAY]: 4,
	[DigestTimingDay.FRIDAY]: 5,
	[DigestTimingDay.SATURDAY]: 6,
};

const NAME_TO_WEEK_NUMBER: Record<MonthlyTimingKey, number> = {
	firstWeek: 1,
	secondWeek: 2,
	thirdWeek: 3,
	fourthWeek: 4,
	lastWeek: 5,
};

/**
 * Converts an insane API response to a slightly more sane value for internal use
 *
 * @param input String of integers in pairs of the format {WeekOfMonth}{DayOfWeek}...
 * such that "1224" means "week 1, Monday" (Weeks are 1-indexed, days are 0-indexed)
 * @returns A relatively sane object
 */
export function toDigestMonthlyTiming(input: string | undefined): DigestMonthlyTiming {
	const chunks = chunk((input || '').split(''), 2);
	const output: DigestMonthlyTiming = createBlankMonthlyTiming();
	chunks.map(([weekString, dayString]): [keyof DigestMonthlyTiming, DigestTimingDay] => [
		WEEK_NUMBER_TO_NAME[weekString],
		DAY_OF_WEEK_DIGIT_TO_NAME[dayString],
	])
		.forEach(([week, day]) => {
			output[week].add(day);
		});
	return output;
}

export function toApiMonthlyTiming(input: DigestMonthlyTiming): string {
	return allMonthlyTimingKeys
		.flatMap((week) => {
			const weekNumber = NAME_TO_WEEK_NUMBER[week];
			return Array.from(input[week])
				.map((day) => NAME_TO_DAY_OF_WEEK_DIGIT[day])
				.flatMap((dayDigit) => [weekNumber, dayDigit]);
		})
		.join('');
}

export function toApiUpdatePayload(id: string, groupId: string, input: DigestFormOutput): UpdateDigest {
	return {
		id,
		groupId,
		startDate: isoDateToDigestDate(input.startDate),
		timingMonthly: toApiMonthlyTiming(input.timingMonthly),
		timingPeriodic: input.timingPeriodic,
	};
}

const DEFAULT_NUMBER_OF_DAYS_IN_PAST_EVENT_CUTOFF = 15;
const DEFAULT_NUMBER_OF_MONTHS_IN_FUTURE_EVENT_CUTOFF = 3;
const DEFAULT_DIGEST_SEND_TIME = '08:00:00';
const DEFAULT_DIGEST_MONITOR = {
	name: 'TimedRight Member Service',
	email: 'member_service@timedright.com',
};

export function toApiCreatePayload(group: Group, input: DigestFormOutput): CreateDigest {
	return {
		// Defaults
		groupId: group.id,
		digestName: getDefaultDigestName(group),
		endDate: isoDateToDigestDate(getDefaultDigestEndDate()),
		timeOfDay: DEFAULT_DIGEST_SEND_TIME,
		digestMonitors: [DEFAULT_DIGEST_MONITOR],
		numberOfDaysInPastEventCutoff: DEFAULT_NUMBER_OF_DAYS_IN_PAST_EVENT_CUTOFF,
		numberOfMonthsInFutureEventCutoff: DEFAULT_NUMBER_OF_MONTHS_IN_FUTURE_EVENT_CUTOFF,
		includeEvents: true,
		includeDiscussions: true,
		includeFileShares: true,
		includeMembers: true,
		includeTimesOnGroupActivity: true,
		isFrench: false,
		// Custom values
		startDate: isoDateToDigestDate(input.startDate),
		timingMonthly: toApiMonthlyTiming(input.timingMonthly),
		timingPeriodic: input.timingPeriodic,
	};
}

function getDefaultDigestEndDate(): string {
	return addYears(new Date(), 100).toISOString();
}

function getDefaultDigestName(group: Group): string {
	return `${group.name} Digest`;
}

export function ensureFutureEndDate(endDate: string): string {
	if (isFuture(new Date(endDate))) {
		return endDate;
	}
	return getDefaultDigestEndDate();
}
